import React from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  PrecisionManufacturing as MachineIcon,
  ChevronRight as ChevronRightIcon,
  PublishOutlined,
  LocalOfferOutlined,
  HourglassBottomRounded as WaitingIcon,
  AssignmentTurnedInOutlined,
  BuildOutlined,
  StarBorder,
  ArchiveOutlined,
  UnarchiveOutlined,
  PublicOutlined,
  LockOutlined,
} from "@mui/icons-material";
import useTranslation from "../../../shared/hooks/useTranslation";
import { ServiceRequest, ServiceRequestStatusCode } from "../models/models";
import { createStatusLabelMap } from "../utils/statusMaps";
import { dt } from "../../../shared/themes/redesignTokens";
import RedesignStatusBadge from "../../../shared/components/redesign/RedesignStatusBadge";
import ServiceLifecycleTracker from "../../shared/components/ServiceLifecycleTracker";
import { getOwnerLifecycleStages } from "../../shared/utils/lifecycleStages";
import { srStatusBadgeColors, getGuidanceInfo, leftBorderColors } from "./ServiceRequestCard";
import dayjs from "dayjs";

// -- Props (same shape as ServiceRequestCard) --
interface ServiceRequestCardCompactProps {
  item: ServiceRequest;
  index: number;
  formatCurrency: (value: number) => string;
  onClick: (item: ServiceRequest) => void;
  onPublish?: (item: ServiceRequest) => void;
  onViewOffers?: (item: ServiceRequest) => void;
  onConfirmCompletion?: (item: ServiceRequest) => void;
  onLeaveReview?: (item: ServiceRequest) => void;
  onArchive?: (item: ServiceRequest) => void;
  onUnarchive?: (item: ServiceRequest) => void;
}

// -- Pill CTA (24px radius, gradient support — same pattern as OfferCardCompact) --
const PillCTA: React.FC<{
  label: string; color: string; hoverColor?: string; onClick?: () => void;
  icon?: React.ReactNode; outline?: boolean; pulse?: boolean; gradient?: boolean;
  isIndicator?: boolean;
}> = ({ label, color, hoverColor, onClick, icon, outline, pulse, gradient, isIndicator }) => (
  <Box
    onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    sx={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: "5px",
      ...(gradient ? { background: "linear-gradient(135deg, #3B82F6, #2563EB)", color: "#FFF" }
        : isIndicator ? { backgroundColor: `${color}14`, color, border: `1px solid ${color}33` }
        : { backgroundColor: outline ? "transparent" : color, color: outline ? color : "#FFF" }),
      border: outline ? `1.5px solid ${color}` : isIndicator ? undefined : "none",
      borderRadius: "24px",
      padding: "9px 16px", fontSize: "12px", fontWeight: 700, fontFamily: dt.font,
      cursor: isIndicator ? "default" : "pointer",
      mt: "auto", whiteSpace: "nowrap", transition: "all 0.2s ease",
      ...(pulse && { animation: "offerRing 2s ease-in-out infinite" }),
      ...(!isIndicator && {
        "&:hover": {
          backgroundColor: outline ? `${color}14` : (hoverColor || color),
          transform: "translateY(-1px)",
          ...(outline && { borderColor: hoverColor || color }),
          ...(gradient && { background: "linear-gradient(135deg, #2563EB, #1D4ED8)" }),
        },
      }),
    }}
  >
    {icon}{label}{!isIndicator && <ChevronRightIcon sx={{ fontSize: "14px" }} />}
  </Box>
);

// -- Guidance text renderer (makes boldName bold within text) --
const GuidanceText: React.FC<{ text: string; boldName?: string }> = ({ text, boldName }) => {
  if (!boldName || !text.includes(boldName)) return <>{text}</>;
  const idx = text.indexOf(boldName);
  return <>{text.slice(0, idx)}<Box component="span" sx={{ fontWeight: 700 }}>{boldName}</Box>{text.slice(idx + boldName.length)}</>;
};

// -- Label style shorthand --
const labelSx = {
  fontSize: "10px", fontWeight: 600, textTransform: "uppercase" as const,
  letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font,
  whiteSpace: "nowrap" as const, flexShrink: 0,
};

// -- ServiceRequestCardCompact --
const ServiceRequestCardCompact: React.FC<ServiceRequestCardCompactProps> = ({
  item, index, formatCurrency, onClick,
  onPublish, onViewOffers, onConfirmCompletion, onLeaveReview, onArchive, onUnarchive,
}) => {
  const { translate } = useTranslation(["servicing", "common"]);
  const navigate = useNavigate();
  const isPhone = useMediaQuery("(max-width:768px)");
  const S = ServiceRequestStatusCode;

  const c = srStatusBadgeColors[item.statusCode] || srStatusBadgeColors[S.archived];
  const borderColor = leftBorderColors[item.statusCode] || "#D1D5DB";
  const statusLabelMap = createStatusLabelMap(translate);
  const guidance = getGuidanceInfo(item, translate);

  const hasAcceptedOffer = !!item.acceptedOffer;
  const offersCount = item.offersCount ?? 0;
  const newOffersCount = item.newOffersCount ?? 0;
  const hasReview = item.acceptedOffer?.reviewRating != null;

  const machine = item.machine;
  const machineSrc = machine?.imageUrl || machine?.catalogImageUrl;
  const machineName = machine?.brandName ? `${machine.brandName} ${machine.name}` : machine?.name;

  // Service date: confirmed arrival date or preferred date
  const serviceDate = item.acceptedOffer?.confirmedArrivalDate || item.serviceStartDate;
  const tf = item.acceptedOffer?.confirmedArrivalTimeFrom?.slice(0, 5);
  const tt = item.acceptedOffer?.confirmedArrivalTimeTo?.slice(0, 5);
  const tw = tf && tt ? `${tf}\u2013${tt}` : "";

  // Price: accepted offer total or budget display
  const priceValue = item.acceptedOffer?.totalEstimatedCost
    || item.lowestOfferPrice
    || item.fixedBudget
    || 0;

  const renderCTA = () => {
    switch (item.statusCode) {
      case S.draft:
        return (
          <PillCTA
            label={translate("servicing:card.publish")}
            color="#8E44AD" hoverColor="#7D3C98"
            onClick={() => onPublish?.(item)}
            icon={<PublishOutlined sx={{ fontSize: 14 }} />}
          />
        );
      case S.openForOffers:
        if (offersCount > 0) {
          return (
            <PillCTA
              label={`${translate("servicing:card.viewOffers")} (${offersCount})`}
              color="#2563EB" hoverColor="#1D4ED8"
              onClick={() => onViewOffers?.(item)}
              icon={<LocalOfferOutlined sx={{ fontSize: 14 }} />}
              pulse={newOffersCount > 0}
            />
          );
        }
        return (
          <PillCTA
            label={translate("servicing:card.awaitingOffers")}
            color="#6B7280" isIndicator
            icon={<WaitingIcon sx={{ fontSize: 14, animation: "spin 2s linear infinite" }} />}
          />
        );
      case S.negotiating:
        return (
          <PillCTA
            label={translate("servicing:card.reviewOffers")}
            color="#8B5CF6" hoverColor="#7C3AED" outline
            onClick={() => onViewOffers?.(item)}
            icon={<LocalOfferOutlined sx={{ fontSize: 14 }} />}
          />
        );
      case S.awaitingProviderConfirmation:
        return (
          <PillCTA
            label={translate("servicing:card.awaitingConfirmation")}
            color="#F59E0B" isIndicator
            icon={<WaitingIcon sx={{ fontSize: 14, animation: "spin 2s linear infinite" }} />}
          />
        );
      case S.inProgress:
        return (
          <PillCTA
            label={translate("servicing:card.workInProgress")}
            color="#2563EB" isIndicator
            icon={<BuildOutlined sx={{ fontSize: 14 }} />}
          />
        );
      case S.awaitingCompletionConfirmation:
        return (
          <PillCTA
            label={translate("servicing:card.confirmCompletion")}
            color="#E67E22" hoverColor="#D35400"
            onClick={() => onConfirmCompletion?.(item)}
            icon={<AssignmentTurnedInOutlined sx={{ fontSize: 14 }} />}
            pulse
          />
        );
      case S.completed:
        if (hasReview) {
          return (
            <PillCTA
              label={translate("servicing:card.archive")}
              color="#6B7280" hoverColor="#4B5563" outline
              onClick={() => onArchive?.(item)}
              icon={<ArchiveOutlined sx={{ fontSize: 14 }} />}
            />
          );
        }
        return (
          <PillCTA
            label={translate("servicing:card.leaveReview")}
            color="#059669" hoverColor="#047857" outline
            onClick={() => onLeaveReview?.(item)}
            icon={<StarBorder sx={{ fontSize: 14 }} />}
          />
        );
      case S.archived:
        return (
          <PillCTA
            label={translate("servicing:card.unarchive")}
            color="#27AE60" hoverColor="#2ECC71"
            onClick={() => onUnarchive?.(item)}
            icon={<UnarchiveOutlined sx={{ fontSize: 14 }} />}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      onClick={() => onClick(item)}
      sx={{
        backgroundColor: dt.cardBg, borderRadius: dt.radiusSm, border: `1px solid ${dt.cardBorder}`,
        borderLeft: `3px solid ${borderColor}`, cursor: "pointer", boxShadow: dt.cardShadow,
        overflow: "hidden", width: "100%", boxSizing: "border-box",
        transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
        animation: `cardReveal 0.4s cubic-bezier(0.4,0,0.2,1) ${index * 0.04}s both`,
        "@keyframes cardReveal": {
          "0%": { opacity: 0, transform: "translateY(12px) scale(0.98)" },
          "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
        },
        "&:hover": { boxShadow: dt.cardShadowHover, transform: "translateY(-1px)" },
        display: "flex", flexDirection: isPhone ? "column" : "row",
      }}
    >
      {/* LEFT ZONE */}
      <Box sx={{ flex: 1, padding: isPhone ? "12px" : "14px 18px 14px 16px", minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
        {/* Row 1: Issue headline + SR ref + visibility icon */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", mb: "4px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flex: 1 }}>
            <Box sx={{ color: dt.textPrimary, fontWeight: 700, fontSize: "16px", fontFamily: dt.font, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
              {item.issue || "\u2014"}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
            {item.visibilityCode === 2 ? (
              <LockOutlined sx={{ fontSize: "14px", color: "#27AE60" }} />
            ) : (
              <PublicOutlined sx={{ fontSize: "14px", color: dt.textMuted }} />
            )}
            <Box sx={{ color: dt.textSecondary, fontSize: "11px", fontFamily: dt.fontMono, letterSpacing: "0.2px", whiteSpace: "nowrap" }}>
              {item.internalNumber}{item.createdDate && <>{" \u00B7 "}{dayjs(item.createdDate).format("DD MMM, YYYY")}</>}
            </Box>
          </Box>
        </Box>

        {/* Row 2: Provider info (from accepted offer) or offers count */}
        {hasAcceptedOffer ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden" }}>
            {item.acceptedOffer?.providerLogoUrl ? (
              <Box component="img" src={item.acceptedOffer.providerLogoUrl} alt="" sx={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `1px solid ${dt.cardBorder}` }} />
            ) : (
              <Box sx={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: dt.blueSoft, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Box sx={{ fontSize: "11px", fontWeight: 700, color: dt.blue, fontFamily: dt.font }}>{item.acceptedOffer?.providerName?.charAt(0)?.toUpperCase() || "?"}</Box>
              </Box>
            )}
            <Box
              component="span"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (item.acceptedOffer?.providerId) navigate(`/companies/${item.acceptedOffer.providerId}`);
              }}
              sx={{ color: dt.textLink, fontWeight: 500, fontSize: "13px", fontFamily: dt.font, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            >{item.acceptedOffer?.providerName}</Box>
          </Box>
        ) : (
          offersCount > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden" }}>
              <Box sx={{ fontSize: "12px", fontWeight: 600, color: dt.blue, fontFamily: dt.font }}>
                {offersCount} {translate("servicing:card.offers")}
              </Box>
              {newOffersCount > 0 && (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "3px", backgroundColor: "#FEF9E7", color: "#F59E0B", borderRadius: "10px", padding: "2px 8px", fontSize: "11px", fontWeight: 600, fontFamily: dt.fontMono, whiteSpace: "nowrap", flexShrink: 0 }}>
                  {newOffersCount} {translate("servicing:newOffers")}
                </Box>
              )}
            </Box>
          )
        )}

        {/* Row 3: Machine (no label) */}
        {machine && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden" }}>
            {machineSrc ? (
              <Box component="img" src={machineSrc} alt="" sx={{ width: 36, height: 36, borderRadius: dt.radiusSm, border: `1px solid ${dt.cardBorder}`, objectFit: "cover", flexShrink: 0 }} />
            ) : (
              <Box sx={{ width: 36, height: 36, borderRadius: dt.radiusSm, border: `1px solid ${dt.cardBorder}`, backgroundColor: dt.inputBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <MachineIcon sx={{ fontSize: 18, color: dt.textMuted }} />
              </Box>
            )}
            <Box
              component="span"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); machine?.id && navigate(`/machinery/${machine.id}`); }}
              sx={{ color: dt.textLink, fontSize: "12px", fontFamily: dt.font, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            >
              {machineName}
            </Box>
          </Box>
        )}

        {/* Row 4: Service date + Price */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          {serviceDate ? (
            <Box sx={{ fontSize: "12px", fontFamily: dt.font, display: "flex", alignItems: "center", gap: "4px" }}>
              <Box component="span" sx={labelSx}>{translate("servicing:card.serviceDate")}</Box>
              <Box component="span" sx={{ color: dt.textPrimary, fontWeight: 600 }}>{dayjs(serviceDate).format("MMM DD, YYYY")}</Box>
              {tw && <Box component="span" sx={{ color: dt.textMuted, fontFamily: dt.fontMono, fontSize: "11px" }}>{"\u00B7 "}{tw}</Box>}
            </Box>
          ) : <Box />}
          {priceValue > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Box component="span" sx={labelSx}>{hasAcceptedOffer ? translate("servicing:price") : translate("servicing:card.budget")}</Box>
              <Box sx={{ backgroundColor: "#F0FDF4", padding: "3px 10px", borderRadius: "6px" }}>
                <Box component="span" sx={{ color: dt.textPrimary, fontWeight: 700, fontSize: "15px", fontFamily: dt.fontMono, whiteSpace: "nowrap" }}>{formatCurrency(priceValue)}</Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* RIGHT ZONE (200px fixed) */}
      <Box sx={{ flex: isPhone ? "none" : "0 0 200px", maxWidth: isPhone ? "100%" : "200px", display: "flex", flexDirection: "column", padding: "14px", borderLeft: isPhone ? "none" : `1px solid ${dt.cardBorder}`, borderTop: isPhone ? `1px solid ${dt.cardBorder}` : "none", gap: "6px" }}>
        <RedesignStatusBadge label={statusLabelMap[item.statusCode] || ""} colorConfig={c} />
        {guidance && (
          <Box sx={{ fontSize: "11px", color: dt.textSecondary, fontFamily: dt.font, lineHeight: 1.4 }}>
            <GuidanceText text={guidance.text} boldName={guidance.boldName} />
          </Box>
        )}
        {item.statusCode !== S.archived && item.statusCode !== S.draft && (
          <ServiceLifecycleTracker stages={getOwnerLifecycleStages(item.statusCode)} variant="compact" />
        )}
        {renderCTA()}
      </Box>
    </Box>
  );
};

export default ServiceRequestCardCompact;
